import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiServices from "../Services/ApiServices";


import { requestForToken } from "../../config/firebase";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as yup from "yup";
import {
  faUser,
  faLock,
  faEye,
  faEyeSlash,
  faArrowRight,
  faArrowLeft,
  faRocket,
  faGraduationCap,
  faLightbulb
} from "@fortawesome/free-solid-svg-icons";
import { storageHelper } from '../../Helpers/storageHelper';

const SignIn = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email_or_phone: "", password: "", role: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ email_or_phone: "", password: "", role: "" });
  const [touched, setTouched] = useState({ email_or_phone: false, password: false, role: false });

  // Validation Schema
  const validationSchema = yup.object().shape({
    email_or_phone: yup.string().trim().required("البريد الالكتروني أو رقم الهاتف مطلوب"),
    password: yup.string().required("كلمة المرور مطلوبة"),
    role: yup.number().required("الدور مطلوب"),
  });

  const handleChange = (e) => {
    const value = e.target.name === 'role' ? Number(e.target.value) : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
    if (fieldErrors[e.target.name]) {
      setFieldErrors((prev) => ({ ...prev, [e.target.name]: "" }));
    }
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    let testedValue = value;

    if (name === "email_or_phone") {
      testedValue = value.trim();
      setFormData((prev) => ({ ...prev, [name]: testedValue }));
    }

    setTouched((prev) => ({ ...prev, [name]: true }));
    try {
      await validationSchema.validateAt(name, { ...formData, [name]: testedValue });
    } catch (valErr) {
      setFieldErrors((prev) => ({ ...prev, [name]: valErr.message }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    let validData;
    try {
      validData = await validationSchema.validate(formData, { abortEarly: false });
    } catch (validationError) {
      const newErrors = {};
      validationError.inner.forEach((issue) => {
        newErrors[issue.path] = issue.message;
      });
      setFieldErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const response = await ApiServices.post("LoginUser", validData);
      const { data } = response;

      // Clear previous super admin OTP flag on fresh login
      storageHelper.removeItem("super_admin_otp_verified");

      // Storage
      const items = [
        { key: "token", value: data.token },
        { key: "full_name", value: data.full_name },
        { key: "user_name", value: data.user_name },
        { key: "phone", value: data.phone },
        { key: "user_class", value: data.user_class },
        { key: "user_class_name", value: data.user_class_name },
        { key: "user_type", value: data.user_type },
        { key: "user_id", value: data.user_id },
        { key: "user_image", value: data.image },
        { key: "user_email", value: data.email },
        { key: "to_teacher", value: data.to_teacher },
        { key: "material", value: data.material },
      ];

      items.forEach(item => {
        if (item.value !== null && item.value !== undefined) {
          storageHelper.setItem(item.key, item.value, undefined, data.expire_on);
        }
      });

      // Notification Setup
      try {
        if ("Notification" in window) {
          const permission = await Notification.requestPermission();
          if (permission === "granted") {
            const token = await requestForToken();
            if (token) {
              await ApiServices.put("User/fcm/update", { fcm_token: token }).catch(() => { });
            }
          }
        }
      } catch (ignore) { }

      const userType = data.user_type;
      if (userType === "superadmin" || userType === "SuperAdmin") {
        storageHelper.setItem("super_admin_otp_verified", "true");
        navigate("/admin/dashboard", { replace: true });
      } else {
        navigate("/");
      }

    } catch (err) {
      const msg = err.msg || "حدث خطأ غير متوقع";
      setError(msg);
      if (err.dynamic_data !== null && err.dynamic_data !== undefined && validData.role !== 3) {
        if (typeof err.dynamic_data === 'object' && err.dynamic_data.verify_otp === true) {
          navigate("/otp", { state: err.dynamic_data });
        } else {
          const emailVal = err.dynamic_data?.email || (typeof err.dynamic_data === "string" && err.dynamic_data.includes("@") ? err.dynamic_data : "");
          const phoneVal = err.dynamic_data?.phone || (typeof err.dynamic_data === "string" && !err.dynamic_data.includes("@") ? err.dynamic_data : "");
          navigate("/otp", { state: { email: emailVal, phone: phoneVal, personal_name: err.dynamic_data?.full_name || err.dynamic_data?.fullName || "" } });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signin-modern-wrapper">
      <div className="signin-modern-container">
        
        {/* Left Side - Brand & Visual */}
        <motion.div 
          className="brand-section"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="brand-content">
            <div className="logo-container">
              <img src="../../assets/images/new_log_design.svg" alt="EMt7any Logo" className="logo" />
            </div>
            <h1 className="hero-text">
              انطلق نحو <span className="highlight-text">النجاح</span><br/> بثقة وإبداع
            </h1>
            <p className="hero-subtext">
              منصتك الأولى للتعلم التفاعلي، انضم الآن وابدأ رحلتك التعليمية بطريقة عصرية ومبتكرة.
            </p>
            <div className="floating-elements">
               <motion.div animate={{ y: [0, -15, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="float-icon fi-1"><FontAwesomeIcon icon={faRocket} /></motion.div>
               <motion.div animate={{ y: [0, 20, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="float-icon fi-2"><FontAwesomeIcon icon={faLightbulb} /></motion.div>
               <motion.div animate={{ y: [0, -20, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }} className="float-icon fi-3"><FontAwesomeIcon icon={faGraduationCap} /></motion.div>
            </div>
          </div>
          <div className="brand-overlay"></div>
        </motion.div>

        {/* Right Side - Form */}
        <div className="form-section">
          <motion.div 
            className="form-box"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <button type="button" className="back-action" onClick={() => navigate("/")}>
              <FontAwesomeIcon icon={faArrowLeft} />
              العودة للرئيسية
            </button>

            <div className="form-header">
              <h2>تسجيل الدخول</h2>
              <p>مرحباً بك مجدداً! أدخل بياناتك للمتابعة.</p>
            </div>

            <form onSubmit={handleSubmit} className="modern-form">
              
              <div className="input-group">
                <label>نوع الحساب</label>
                <div className={`input-wrapper ${touched.role && fieldErrors.role ? 'has-error' : ''}`}>
                  <div className="input-icon"><FontAwesomeIcon icon={faUser} /></div>
                  <select name="role" value={formData.role} onChange={handleChange} onBlur={handleBlur}>
                    <option value={1}>طالب</option>
                    <option value={2}>معلم</option>
                    <option value={3}>مدير النظام</option>
                  </select>
                </div>
                {touched.role && fieldErrors.role && <span className="error-text">{fieldErrors.role}</span>}
              </div>

              <div className="input-group">
                <label>البريد الإلكتروني أو رقم الهاتف</label>
                <div className={`input-wrapper ${touched.email_or_phone && fieldErrors.email_or_phone ? 'has-error' : ''}`}>
                  <div className="input-icon"><FontAwesomeIcon icon={faUser} /></div>
                  <input type="text" name="email_or_phone" value={formData.email_or_phone} onChange={handleChange} onBlur={handleBlur} placeholder="أدخل بريدك أو رقمك" />
                </div>
                {touched.email_or_phone && fieldErrors.email_or_phone && <span className="error-text">{fieldErrors.email_or_phone}</span>}
              </div>

              <div className="input-group">
                <label>كلمة المرور</label>
                <div className={`input-wrapper ${touched.password && fieldErrors.password ? 'has-error' : ''}`}>
                  <div className="input-icon"><FontAwesomeIcon icon={faLock} /></div>
                  <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} onBlur={handleBlur} placeholder="••••••••" />
                  <button type="button" className="toggle-password" onClick={() => setShowPassword(!showPassword)}>
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {touched.password && fieldErrors.password && <span className="error-text">{fieldErrors.password}</span>}
              </div>

              <div className="form-actions">
                <button type="button" className="forgot-password" onClick={() => navigate("/forgot-password")}>
                  هل نسيت كلمة المرور؟
                </button>
              </div>

              {error && <div className="global-error">{error}</div>}

              <button type="submit" disabled={loading} className="submit-btn">
                {loading ? <div className="loader-ring"></div> : (
                  <>
                    <span>دخول</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </>
                )}
              </button>

            </form>
            
            <div className="form-footer">
              ليس لديك حساب؟ <button type="button" className="register-link" onClick={() => navigate("/register-teacher")}>أنشئ حساباً كمعلم</button>
            </div>
          </motion.div>
        </div>

      </div>

      <style jsx>{`
        .signin-modern-wrapper * {
          box-sizing: border-box;
          font-family: 'Zain', sans-serif;
        }
        
        .signin-modern-wrapper {
          min-height: 100vh;
          width: 100%;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          direction: rtl;
        }

        .signin-modern-container {
          width: 100%;
          max-width: 1200px;
          min-height: 700px;
          background: #ffffff;
          border-radius: 32px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
          display: flex;
          overflow: hidden;
          position: relative;
        }

        .brand-section {
          flex: 1.2;
          background: url('/assets/images/service-01.png') center/cover no-repeat;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 40px;
          color: #ffffff;
          overflow: hidden;
        }
        @media (max-width: 968px) {
          .brand-section { display: none; }
          .signin-modern-container {
            max-width: 500px;
            min-height: auto;
          }
        }

        .brand-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(79, 70, 229, 0.9) 0%, rgba(124, 58, 237, 0.95) 100%);
          z-index: 1;
        }

        .brand-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 450px;
        }

        .logo-container {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(16px);
          padding: 20px;
          border-radius: 24px;
          display: inline-block;
          margin-bottom: 40px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }
        
        .logo { width: 140px; filter: brightness(0) invert(1); }

        .hero-text {
          font-size: 3.2rem;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 24px;
          text-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .highlight-text { color: #fbbf24; }

        .hero-subtext {
          font-size: 1.15rem;
          line-height: 1.7;
          opacity: 0.9;
          font-weight: 500;
        }

        .floating-elements {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 0;
        }
        .float-icon {
          position: absolute;
          color: rgba(255, 255, 255, 0.15);
        }
        .fi-1 { font-size: 6rem; top: 15%; right: 10%; transform: rotate(15deg); }
        .fi-2 { font-size: 5rem; bottom: 20%; left: 15%; transform: rotate(-15deg); }
        .fi-3 { font-size: 4rem; top: 50%; left: 10%; }

        .form-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          background: #ffffff;
          width: 100%;
        }
        @media (max-width: 480px) {
          .signin-modern-wrapper { padding: 0; align-items: flex-start; justify-content: center; }
          .signin-modern-container { 
            border-radius: 0; 
            min-height: 100vh; 
            max-width: 100%;
            width: 100%;
            box-shadow: none;
          }
          .form-section { 
            padding: 40px 20px; 
            justify-content: flex-start;
            align-items: center;
          }
          .form-box { margin-top: 20px; }
          .form-header { margin-bottom: 24px; text-align: center; }
          .form-header h2 { font-size: 1.8rem; }
          .modern-form { gap: 16px; }
          .input-wrapper { height: 50px; }
          .submit-btn { height: 50px; font-size: 1.1rem; }
        }

        .form-box {
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
        }

        .back-action {
          display: flex;
          align-items: center;
          gap: 10px;
          background: transparent;
          border: none;
          color: #64748b;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          margin-bottom: 30px;
          padding: 0;
          transition: 0.3s ease;
        }
        .back-action:hover { color: #4f46e5; transform: translateX(5px); }

        .form-header { margin-bottom: 40px; }
        .form-header h2 { font-size: 2.2rem; font-weight: 800; color: #0f172a; margin-bottom: 10px; }
        .form-header p { font-size: 1.05rem; color: #64748b; }

        .modern-form { display: flex; flex-direction: column; gap: 24px; width: 100%; }

        .input-group { display: flex; flex-direction: column; gap: 8px; width: 100%; }
        
        .input-group label {
          font-size: 0.95rem;
          font-weight: 700;
          color: #334155;
        }

        .input-wrapper {
          display: flex;
          align-items: center;
          width: 100%;
          height: 56px;
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 16px !important;
          padding: 0 16px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .input-wrapper:focus-within {
          background: #ffffff;
          border-color: #6366f1;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
        }

        .input-wrapper.has-error {
          border-color: #ef4444;
          background: #fef2f2;
        }

        .input-icon {
          width: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          font-size: 1.2rem;
          margin-left: 12px;
          transition: 0.3s;
        }
        .input-wrapper:focus-within .input-icon { color: #6366f1; }
        .input-wrapper.has-error .input-icon { color: #ef4444; }

        .input-wrapper input,
        .input-wrapper select {
          flex: 1;
          height: 100%;
          border: none;
          background: transparent;
          font-size: 1.05rem;
          color: #1e293b;
          outline: none;
          min-width: 0;
          margin: 0;
          padding: 0;
          font-family: inherit;
        }

        .input-wrapper select {
          cursor: pointer;
          -webkit-appearance: none;
          appearance: none;
        }

        .input-wrapper input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 50px #f8fafc inset !important;
          -webkit-text-fill-color: #1e293b !important;
        }
        .input-wrapper:focus-within input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 50px #ffffff inset !important;
        }

        .toggle-password {
          background: transparent;
          border: none;
          color: #94a3b8;
          font-size: 1.1rem;
          cursor: pointer;
          padding: 0 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: 0.2s;
        }
        .toggle-password:hover { color: #475569; }

        .error-text {
          color: #ef4444;
          font-size: 0.85rem;
          font-weight: 600;
          margin-top: 4px;
          display: flex;
          align-items: center;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          margin-top: -8px;
        }

        .forgot-password {
          background: none;
          border: none;
          color: #6366f1;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          transition: 0.2s;
        }
        .forgot-password:hover { color: #4f46e5; text-decoration: underline; }

        .global-error {
          background: #fef2f2;
          color: #b91c1c;
          padding: 16px;
          border-radius: 12px;
          font-weight: 600;
          text-align: center;
          font-size: 0.95rem;
          border: 1px solid #fecaca;
        }

        .submit-btn {
          height: 56px;
          width: 100%;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 1.15rem;
          font-weight: 800;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          transition: all 0.3s ease;
          box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3);
          margin-top: 10px;
        }
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 12px 24px rgba(99, 102, 241, 0.4);
        }
        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .loader-ring {
          width: 26px;
          height: 26px;
          border: 3px solid rgba(255,255,255,0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: spin 1s infinite linear;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .form-footer {
          margin-top: 32px;
          text-align: center;
          font-size: 1rem;
          color: #64748b;
          font-weight: 500;
        }
        .register-link {
          background: none;
          border: none;
          color: #6366f1;
          font-weight: 800;
          font-size: 1rem;
          cursor: pointer;
          transition: 0.2s;
          padding: 0 4px;
        }
        .register-link:hover { color: #4f46e5; text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default SignIn;
